// Approval workflow administration (module 07, PB-07-12, PB-17-09; PD-049..PD-053).
import { z } from 'zod';

const Uuid = z.uuid();
const Key = z.string().trim().regex(/^[a-z][a-z0-9_-]{0,39}$/, 'v.policyKey');
const Label = z.object({ en: z.string().trim().min(1, 'v.required').max(80, 'v.tooLong'), ar: z.string().trim().max(80, 'v.tooLong').optional() });

export const CreateApproverGroupRequest = z.object({ key: Key, label: Label });
export const UpdateApproverGroupRequest = z.object({ expectedVersion: z.number().int().min(1), label: Label.optional(), active: z.boolean().optional() });
export const AddGroupMemberRequest = z.object({
  userId: Uuid,
  siteId: Uuid.nullable().optional(),
  department: z.string().trim().max(120, 'v.tooLong').nullable().optional(),
  locationId: Uuid.nullable().optional(),
  categoryCode: Key.nullable().optional(),
});

export interface ApproverGroupMemberResponse {
  readonly id: string;
  readonly user: { readonly id: string; readonly displayName: string };
  readonly siteId: string | null;
  readonly department: string | null;
  readonly location: { readonly id: string; readonly name: string } | null;
  readonly categoryCode: string | null;
  /** Whether the person currently holds `approval.decide` where the scope applies. */
  readonly canDecideSomewhere: boolean;
  readonly addedAt: string;
}

export interface ApproverGroupResponse {
  readonly id: string;
  readonly key: string;
  readonly label: { readonly en: string; readonly ar?: string | undefined };
  readonly active: boolean;
  readonly version: number;
  readonly members: readonly ApproverGroupMemberResponse[];
}

export const CreateDelegationRequest = z
  .object({
    /** Omitted = the caller delegates their own approvals; otherwise a coordinator acts for someone. */
    fromUserId: Uuid.optional(),
    toUserId: Uuid,
    siteId: Uuid.nullable().optional(),
    startsAt: z.iso.datetime({ offset: true }),
    endsAt: z.iso.datetime({ offset: true }),
    reason: z.string().trim().min(1, 'v.required').max(500, 'v.tooLong'),
  })
  .superRefine((v, ctx) => {
    if (new Date(v.endsAt) <= new Date(v.startsAt)) ctx.addIssue({ code: 'custom', path: ['endsAt'], message: 'v.endBeforeStart' });
    if (new Date(v.endsAt).getTime() - new Date(v.startsAt).getTime() > 90 * 86_400_000) ctx.addIssue({ code: 'custom', path: ['endsAt'], message: 'v.delegationTooLong' });
  });

export interface DelegationResponse {
  readonly id: string;
  readonly siteId: string | null;
  readonly from: { readonly id: string; readonly displayName: string };
  readonly to: { readonly id: string; readonly displayName: string };
  readonly startsAt: string;
  readonly endsAt: string;
  readonly reason: string;
  readonly revokedAt: string | null;
  readonly active: boolean;
}

export const AssignEscortRequest = z.object({ hostId: Uuid, note: z.string().trim().max(300, 'v.tooLong').nullable().optional(), expectedVersion: z.number().int().min(1) });
export interface EscortResponse {
  readonly id: string;
  readonly host: { readonly id: string; readonly displayName: string };
  readonly assignedAt: string;
  readonly note: string | null;
}
